import type { FeatureContext } from './featureContext';
import type { EntityId } from '../sim/entity';
import { initDuelBowPrompt } from '../duelBowPrompt';
import { classifyDuelChat, chebyshev, DUEL_SWING_RANGE, DUEL_BOW_RANGE } from '../duel';
import {
  hitGruntCue,
  whiffCue,
  duelBowCue,
  deathCryCue,
} from '../audio/combatCue';
import { ARENA_PROJECTILE_TILES_PER_S } from '../sim/projectile';
import { duelSoundVolume, bodySexOf } from './cueVolume';
import { isPvpMenuTarget } from './pvpTarget';

/**
 * Duel combat red (Player duels, PRD #252, ADR-0131): the floating damage number
 * draws in the same red as the combat alert line so a landed hit reads
 * unmistakably as damage. A miss floats nothing (only its whiff sounds), so there
 * is no miss colour.
 */
const COMBAT_DAMAGE_COLOR = 0xdd2222;

// Loudness ceilings for the duel combat cues at distance 0 (Duel slice D,
// ADR-0131), proximity-scaled for onlookers and full for a participant's own
// fight. Hits/deaths punch a little above squeals; the whiff sits below.
const DUEL_HIT_VOLUME = 0.8;
const DUEL_DEATH_VOLUME = 0.9;
const DUEL_WHIFF_VOLUME = 0.5;
// Loudness ceiling for a fired duel bow shot's twang at distance 0 (Fire your bow!,
// ADR-0255), proximity-scaled for onlookers and full for a participant's own fight,
// like the other duel cues. Sits with the hit cue - a bow release reads clearly.
const DUEL_BOW_VOLUME = 0.8;
// Loudness ceiling for the **thunk** of a landed duel arrow at distance 0,
// proximity-scaled like the other duel cues. A touch above the twang - the arrow
// biting home is the payoff beat. Played only on a hit, timed to the arrow's
// arrival at the struck duelist (a miss thuds nothing).
const DUEL_ARROW_HIT_VOLUME = 0.85;

/**
 * The most recent fired duel-bow shot's validity window, so a landed shot's `thunk`
 * (ADR-0255) can be timed to the arrow's arrival. The server enacts the shot in one
 * beat - the shooter's `shoot-bow` clip then, on a hit, the victim's `hit` flinch -
 * so a `hit` arriving within this window of a `shoot-bow` *is* that arrow biting
 * home (a miss sends no flinch, and a melee punch's flinch never follows a
 * `shoot-bow`). The shooter's ~1.2s bow cooldown keeps shots far enough apart that
 * the window can never span two of them.
 */
const DUEL_BOW_HIT_WINDOW_MS = 120;

/** Injectable seams for unit tests; defaults are the real modules. */
export interface DuelInstallDeps {
  initDuelBowPrompt?: typeof initDuelBowPrompt;
}

/**
 * Player duels (PRD #252): the challenge flows (spoken "I wish to duel" / `/duel` /
 * the long-press "Duel <name>" row -> the wager prompt -> `DuelChallenge`), the
 * target's accept/decline prompt, the combat-stance couplings (war-red cursor force
 * + the combat music override - both duelists switch, nobody else's changes), the
 * fire-bow prompt/key (ADR-0255), and the duel combat sounds + floating damage text.
 */
export function installDuel(ctx: FeatureContext, deps: DuelInstallDeps = {}): void {
  // The "Press F to auto-fire your bow!" prompt (ADR-0255; ADR-0279), shown while in a duel and
  // the opponent is out of punch range but within bow range (driven from the
  // snapshot handler below).
  const bowPrompt = (deps.initDuelBowPrompt ?? initDuelBowPrompt)(ctx.ui);

  // The local player is in a live duel's combat stance (Duel slice D, ADR-0131),
  // read from its own replicated `renderable.combatStance` each snapshot. Edges
  // drive the war-red cursor force (via the shared state) and the combat music.
  let duelCombat = false;
  // The local player's current duel opponent (ADR-0255), set from the owner-only
  // `DuelCombat` message when combat opens and cleared when the combat stance drops
  // (any duel end). Drives the fire-bow prompt; the server resolves the actual
  // fire-bow target independently server-side.
  let duelOpponentId: EntityId | undefined;
  // The most recent fired duel-bow shot (see DUEL_BOW_HIT_WINDOW_MS).
  let pendingDuelBowShot: { shooter: EntityId; at: number } | null = null;
  ctx.events.on('connection', () => {
    pendingDuelBowShot = null;
  });

  // Open the duel challenge flow for an already-known target (ADR-0130): the twin
  // of the dice installer's `startDiceWager`, reusing the same wager prompt
  // (default "a duel to the death" copy) and sending a `DuelChallenge` up the
  // socket. Shared by the two entry points - the armed blue-cursor pick and the
  // long-press "Duel" menu item (ADR-0160/0169). Anonymous sessions are capped at
  // a zero-wager honor duel. The server rules on the target (self / rat / NPC /
  // ghost / range / busy) and the wager; an invalid pick fizzles silently.
  const startDuel = (target: EntityId): void => {
    if (ctx.playerId() === undefined) return;
    const opponentName = ctx.world()?.get(target)?.renderable?.name ?? 'that player';
    const gold = ctx.state.gold();
    ctx.state.duelUi.promptWager({
      opponentName,
      // Only reserved (durable) balances can stake; an anonymous session is capped
      // at a zero-wager honor duel, which the prompt enforces via `canStake`.
      maxGold: gold.durable ? gold.amount : 0,
      canStake: gold.durable,
      onConfirm: (wager) => {
        const playerId = ctx.playerId();
        if (playerId !== undefined) {
          ctx.client()?.sendCommand({ kind: 'DuelChallenge', entity: playerId, opponent: target, wager });
        }
      },
    });
  };

  ctx.state.onTargetPick('duel', startDuel);

  // Both "I wish to duel" (spoken, PRD #252) and the `/duel` slash-command alias
  // (ADR-0165) route through the one classifier, so they invoke the exact same
  // duel-initiation path. The spoken phrase arms and **falls through** (still
  // broadcasts so the whole room hears the call-out); `/duel` is consumed here so
  // the raw command never broadcasts as a chat line.
  ctx.chatCommands.intercept((text) => {
    const intent = classifyDuelChat(text);
    if (intent.arm) ctx.state.armTargeting('duel');
    return intent.suppress;
  });

  // The long-press "Duel <name>" row, gated by the shared PvP row filter (after the
  // dice installer's "Roll the dice" row - install order pins today's row order).
  ctx.menu.addEntityMenu((target) => {
    if (!isPvpMenuTarget(ctx.world(), ctx.playerId(), target.id, target.name)) return null;
    return { items: [{ label: `Duel ${target.name}`, onSelect: () => startDuel(target.id) }] };
  });

  // The owner-only duel challenge prompt (ADR-0130): open the accept/decline window
  // naming the challenger and stake, or dismiss it when the server withdraws /
  // expires / resolves the challenge. Accept/Decline send the matching command up
  // the socket; the server escrows, resolves, and notifies.
  ctx.events.on('duelPrompt', (msg) => {
    if (!msg.open) {
      ctx.state.duelUi.dismissChallenge();
      return;
    }
    ctx.state.duelUi.showChallenge({
      challengerName: msg.challengerName,
      wager: msg.wager,
      onAccept: () => {
        const playerId = ctx.playerId();
        if (playerId !== undefined) ctx.client()?.sendCommand({ kind: 'DuelAccept', entity: playerId });
      },
      onDecline: () => {
        const playerId = ctx.playerId();
        if (playerId !== undefined) ctx.client()?.sendCommand({ kind: 'DuelDecline', entity: playerId });
      },
    });
  });

  // The owner-only "here is your duel opponent" signal (ADR-0255), sent when combat
  // opens. Remember the opponent id so the snapshot handler can drive the fire-bow
  // prompt; the id is cleared when the local combat stance drops (any duel end).
  ctx.events.on('duelCombat', (msg) => {
    duelOpponentId = msg.opponent;
  });

  ctx.events.on('snapshot', (world) => {
    const playerId = ctx.playerId();
    // Force the war-red cursor while the local player is in a duel's combat stance
    // (ADR-0131), reverting when it drops. Read from the player's own replicated
    // `renderable.combatStance`; the shared state repaints the cursor immediately.
    const inCombat = playerId !== undefined && world.get(playerId)?.renderable?.combatStance === true;
    if (inCombat !== duelCombat) {
      duelCombat = inCombat;
      ctx.state.setDuelCombat(inCombat);
      // Swap the local player's background music to the looping combat track for the
      // duel's duration, reverting to the login playlist when it ends (ADR-0146). Keyed
      // on this client's own combat stance, so both duelists switch and no one else's
      // music changes - and every duel end path (win/loss/draw/timeout/disconnect/
      // storm-cancel) flows through this same stance drop. Idempotent in the player.
      ctx.music.setCombat(inCombat);
      // The combat stance is the one signal every duel end path drops (ADR-0255), so
      // forget the fire-bow opponent when it clears - the prompt hides below.
      if (!inCombat) duelOpponentId = undefined;
    }

    // Fire-bow prompt (ADR-0255; ADR-0279): show "Press F to auto-fire your bow!" while in a duel and
    // the opponent has kited beyond punch range (DUEL_SWING_RANGE) but is still within
    // bow range (DUEL_BOW_RANGE) - the window where the melee auto-swing no longer lands but
    // toggled bow auto-fire can. Recomputed each snapshot off both live tile positions. The server
    // re-checks range on the actual FireBow, so this only governs the hint, never gates
    // the shot.
    let showBowPrompt = false;
    if (inCombat && duelOpponentId !== undefined && playerId !== undefined) {
      const mePos = world.get(playerId)?.position;
      const oppPos = world.get(duelOpponentId)?.position;
      if (mePos && oppPos) {
        const dist = chebyshev(mePos.x, mePos.y, oppPos.x, oppPos.y);
        showBowPrompt = dist > DUEL_SWING_RANGE && dist <= DUEL_BOW_RANGE;
      }
    }
    if (showBowPrompt) bowPrompt.show();
    else bowPrompt.hide();
  });

  // Fire bow (ADR-0255; Auto-fire the duel bow): F is now a *toggle* that arms/disarms
  // continuous auto-fire at the opponent, the ranged twin of the always-on auto-swing.
  // One `FireBow` is sent per press - the server flips the arm bit (looses the first
  // shot on arm) and then repeats the shot at the legal cadence on its own, so the
  // player no longer presses F per shot and spamming F cannot fire faster (every shot
  // stays server-cooldown-gated). Sent unconditionally: the server owns the real gates
  // (must be in a live duel, opponent in bow range, cooldown ready) and silently drops
  // an out-of-range / cooling / no-duel press, so a stray F is a harmless no-op. The
  // prompt above tells the player when auto-fire will actually land. The opponent is
  // resolved server-side from the shooter's match, so the command carries only the actor.
  ctx.actions.register('fire-bow', {
    down: () => {
      const playerId = ctx.playerId();
      if (playerId !== undefined) ctx.client()?.sendCommand({ kind: 'FireBow', entity: playerId });
    },
  });

  // Duel combat sounds (ADR-0131) ride the action broadcast like the rat cues:
  // a landed blow's flinch clip on the victim sounds the victim's gendered hit
  // grunt; a death clip sounds the loser's gendered death cry. The whiff on a
  // miss rides the CombatText path instead (a miss plays no flinch clip). A
  // swing's punch clip carries no effort sound.
  ctx.events.on('actionAnimation', (msg) => {
    const world = ctx.world();
    const playerId = ctx.playerId();
    if (msg.clip === 'hit') {
      const grunt = hitGruntCue(
        bodySexOf(world, msg.entityId),
        duelSoundVolume(world, playerId, msg.entityId, DUEL_HIT_VOLUME),
      );
      if (grunt) ctx.audio.play(grunt);
      // A flinch right after a fired bow shot is that arrow landing (ADR-0255):
      // schedule the impact thunk for when the arrow reaches the victim, so the thud
      // lands with the visible arrow, not at fire time. Proximity-scaled like the
      // twang; a miss never reaches here (no flinch), so only a hit thuds.
      if (pendingDuelBowShot && performance.now() - pendingDuelBowShot.at <= DUEL_BOW_HIT_WINDOW_MS) {
        const shooterMob = world?.get(pendingDuelBowShot.shooter)?.mobile;
        const victimMob = world?.get(msg.entityId)?.mobile;
        const thunkVol = duelSoundVolume(world, playerId, msg.entityId, DUEL_ARROW_HIT_VOLUME);
        if (shooterMob && victimMob && thunkVol > 0) {
          const tiles = Math.hypot(shooterMob.tileX - victimMob.tileX, shooterMob.tileY - victimMob.tileY);
          const flightMs = (tiles / ARENA_PROJECTILE_TILES_PER_S) * 1000;
          window.setTimeout(() => ctx.audio.play({ sound: 'arrow-hit', volume: thunkVol }), flightMs);
        }
        pendingDuelBowShot = null;
      }
    } else if (msg.clip === 'die-forward' || msg.clip === 'die-back') {
      const cry = deathCryCue(
        bodySexOf(world, msg.entityId),
        duelSoundVolume(world, playerId, msg.entityId, DUEL_DEATH_VOLUME),
      );
      if (cry) ctx.audio.play(cry);
    } else if (msg.clip === 'shoot-bow') {
      // A fired duel bow shot (ADR-0255): the bow twang on the shooter, full for the
      // shooter's own fight and proximity-scaled for onlookers (the duel-cue rule). The
      // arrow flies on hit or miss, so it sounds unconditionally here; a landing hit's
      // get-hit grunt rides the victim's separate `hit` flinch clip above.
      const twang = duelBowCue(duelSoundVolume(world, playerId, msg.entityId, DUEL_BOW_VOLUME));
      if (twang) ctx.audio.play(twang);
      // Remember the shot so an immediately-following `hit` flinch can time its thunk
      // to the arrow's arrival (see the `hit` branch). Overwritten each shot; the
      // cooldown keeps shots well apart.
      pendingDuelBowShot = { shooter: msg.entityId, at: performance.now() };
    }
  });

  // Floating combat text (Duel slice D, ADR-0131): a rising red damage number over
  // a struck duelist. A miss floats NOTHING - only its whiff sounds here (a miss
  // plays no flinch clip, so its sound rides this signal rather than the action
  // broadcast). Ephemeral like the action broadcast. In a live Arena run the same
  // damage float serves the horde bites / projectile hits (the arena installer adds
  // its own vignette handler on this event).
  ctx.events.on('combatText', (msg) => {
    if (msg.outcome === 'damage') {
      if (!ctx.ui.hidden) {
        ctx.renderer()?.showCombatText(msg.entityId, msg.text, COMBAT_DAMAGE_COLOR);
      }
    } else {
      const whiff = whiffCue(duelSoundVolume(ctx.world(), ctx.playerId(), msg.entityId, DUEL_WHIFF_VOLUME));
      if (whiff) ctx.audio.play(whiff);
    }
  });
}
